(() => {
  const productSelector = 'product-info[data-ash-pdp]';
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  document.documentElement.classList.add('ash-pdp-page');

  const enhancePageChrome = () => {
    const productInfo = document.querySelector(productSelector);
    const breadcrumbCurrent = document.querySelector('.m-breadcrumb .m-breadcrumb--item-current');
    breadcrumbCurrent?.setAttribute('aria-current', 'page');
    document.documentElement.classList.toggle(
      'ash-pdp-has-sticky',
      Boolean(productInfo?.querySelector('ash-pdp-sticky')),
    );
  };

  const getSelectedVariant = (productInfo) => {
    const source = productInfo.querySelector('variant-picker [data-selected-variant]');
    if (!source?.textContent.trim()) return null;

    try {
      return JSON.parse(source.textContent);
    } catch (_error) {
      return null;
    }
  };

  const formatVariantPrice = (price) => {
    if (typeof window.formatMoney === 'function') {
      return window.formatMoney(price, window.MinimogSettings?.money_format);
    }
    if (typeof formatMoney === 'function') {
      return formatMoney(price, window.MinimogSettings?.money_format);
    }
    return '';
  };

  const suppressReducedMotionAutoplay = (scope = document) => {
    if (!reducedMotionQuery.matches) return;

    scope.querySelectorAll(`${productSelector} media-gallery`).forEach((gallery) => {
      gallery.dataset.enableVideoAutoplay = 'false';
      gallery.querySelectorAll('deferred-media[data-auto-play]').forEach((media) => {
        media.dataset.autoPlay = 'false';
      });
      window.pauseAllMedia?.(gallery);
    });
  };

  class AshPdpSticky extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.productInfo = this.closest(productSelector);
      this.sectionId = this.dataset.sectionId;
      this.mainForm = this.productInfo?.querySelector(`#product-form-${CSS.escape(this.sectionId)}`);
      this.mainButton = this.mainForm?.querySelector('[name="add"]');
      this.stickyButton = this.querySelector('[data-ash-sticky-submit]');
      this.stickyButtonText = this.querySelector('[data-ash-sticky-button-text]');
      this.variantText = this.querySelector('[data-ash-sticky-variant]');
      this.price = this.querySelector('[data-ash-sticky-price]');
      this.image = this.querySelector('[data-ash-sticky-image]');
      this.visibilityFrame = null;
      this.hideTimer = null;

      if (!this.productInfo || !this.mainForm || !this.mainButton || !this.stickyButton) return;

      this.onScrollOrResize = () => {
        if (this.visibilityFrame) return;
        this.visibilityFrame = requestAnimationFrame(() => {
          this.visibilityFrame = null;
          this.updateVisibility();
        });
      };
      this.onVariantChange = (event) => {
        const currentVariant = this.productInfo.currentVariant || getSelectedVariant(this.productInfo);
        if (event.detail?.variant?.id !== currentVariant?.id) return;
        this.syncVariant(currentVariant);
        requestAnimationFrame(() => this.syncButton());
      };
      this.onDialogToggle = (event) => {
        if (event.detail?.sectionId !== this.sectionId) return;
        this.updateVisibility();
      };
      this.onSubmit = () => {
        if (this.mainButton.disabled) return;
        if (typeof this.mainForm.requestSubmit === 'function') {
          this.mainForm.requestSubmit(this.mainButton);
        } else {
          this.mainButton.click();
        }
      };

      this.stickyButton.addEventListener('click', this.onSubmit);
      window.addEventListener('scroll', this.onScrollOrResize, { passive: true });
      window.addEventListener('resize', this.onScrollOrResize, { passive: true });
      document.addEventListener('variant:changed', this.onVariantChange);
      document.addEventListener('ash:size-guide:toggle', this.onDialogToggle);

      this.buttonObserver = new MutationObserver(() => this.syncButton());
      this.buttonObserver.observe(this.mainButton, {
        attributes: true,
        childList: true,
        characterData: true,
        subtree: true,
      });

      this.syncVariant(getSelectedVariant(this.productInfo));
      this.syncButton();
      this.updateVisibility();
    }

    disconnectedCallback() {
      this.stickyButton?.removeEventListener('click', this.onSubmit);
      window.removeEventListener('scroll', this.onScrollOrResize);
      window.removeEventListener('resize', this.onScrollOrResize);
      document.removeEventListener('variant:changed', this.onVariantChange);
      document.removeEventListener('ash:size-guide:toggle', this.onDialogToggle);
      this.buttonObserver?.disconnect();
      if (this.visibilityFrame) cancelAnimationFrame(this.visibilityFrame);
      if (this.hideTimer) clearTimeout(this.hideTimer);
    }

    syncButton() {
      const sourceText = this.mainButton.querySelector('.m-add-to-cart--text')?.textContent.trim();
      if (sourceText) this.stickyButtonText.textContent = sourceText;

      this.stickyButton.disabled = this.mainButton.disabled;
      this.setAttribute('aria-disabled', String(this.mainButton.disabled));
      this.classList.toggle('is-loading', this.mainButton.classList.contains('loading'));
    }

    syncVariant(variant) {
      if (!variant) return;

      if (this.variantText) {
        const hideVariant = this.dataset.hasOnlyDefaultVariant === 'true';
        this.variantText.hidden = hideVariant;
        if (!hideVariant) this.variantText.textContent = variant.title;
      }

      if (this.price) {
        const formattedPrice = formatVariantPrice(variant.price);
        if (formattedPrice) this.price.innerHTML = formattedPrice;
      }

      const imageSource = variant.featured_media?.preview_image?.src || variant.featured_image?.src;
      if (this.image && imageSource) {
        this.image.removeAttribute('srcset');
        this.image.src = imageSource;
      }
    }

    getHeaderOffset() {
      const cssValue = getComputedStyle(document.documentElement).getPropertyValue('--m-header-height');
      const cssOffset = Number.parseFloat(cssValue);
      if (Number.isFinite(cssOffset)) return Math.max(0, cssOffset);

      const header = document.querySelector('header');
      if (!header) return 0;
      const position = getComputedStyle(header).position;
      return position === 'fixed' || position === 'sticky'
        ? Math.max(0, header.getBoundingClientRect().bottom)
        : 0;
    }

    updateVisibility() {
      const desktop = window.matchMedia('(min-width: 1024px)').matches;
      const enabled = desktop ? this.dataset.showDesktop === 'true' : this.dataset.showMobile === 'true';
      const dialogOpen = Boolean(this.productInfo.querySelector('[data-ash-size-guide][open]'));
      const buttonPassed = this.mainButton.getBoundingClientRect().bottom <= this.getHeaderOffset();

      if (enabled && buttonPassed && !dialogOpen) {
        this.show();
      } else {
        this.hide();
      }
    }

    show() {
      if (this.hideTimer) clearTimeout(this.hideTimer);
      if (!this.hidden && this.classList.contains('is-visible')) return;
      this.hidden = false;
      this.setAttribute('aria-hidden', 'false');
      requestAnimationFrame(() => this.classList.add('is-visible'));
    }

    hide() {
      if (this.hidden) return;
      this.classList.remove('is-visible');
      this.setAttribute('aria-hidden', 'true');
      if (this.hideTimer) clearTimeout(this.hideTimer);
      this.hideTimer = setTimeout(() => {
        if (!this.classList.contains('is-visible')) this.hidden = true;
      }, reducedMotionQuery.matches ? 0 : 260);
    }
  }

  if (!customElements.get('ash-pdp-sticky')) {
    customElements.define('ash-pdp-sticky', AshPdpSticky);
  }

  const setupSizeGuide = (productInfo) => {
    const dialog = productInfo.querySelector('[data-ash-size-guide]');
    if (!dialog || !productInfo.querySelector('[data-open-sizeguide]')) return;

    let returnFocus = null;
    const sectionId = productInfo.dataset.sectionId;
    const getTriggers = () => Array.from(productInfo.querySelectorAll('[data-open-sizeguide]'));

    const prepareTriggers = () => {
      getTriggers().forEach((trigger) => {
        if (trigger.tagName === 'BUTTON') trigger.type = 'button';
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.setAttribute('aria-controls', dialog.id);
        trigger.setAttribute('aria-expanded', String(dialog.open));
      });
    };

    const notify = (open) => {
      document.dispatchEvent(new CustomEvent('ash:size-guide:toggle', {
        detail: { open, sectionId },
      }));
    };

    const openDialog = (trigger) => {
      returnFocus = trigger;
      getTriggers().forEach((item) => item.setAttribute('aria-expanded', 'true'));
      document.body.classList.add('ash-pdp-dialog-open');
      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }
      notify(true);
      dialog.querySelector('[data-ash-size-guide-close]')?.focus();
    };

    const closeDialog = () => {
      if (typeof dialog.close === 'function') {
        dialog.close();
      } else {
        dialog.removeAttribute('open');
        dialog.dispatchEvent(new Event('close'));
      }
    };

    prepareTriggers();
    productInfo.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-open-sizeguide]');
      if (!trigger || !productInfo.contains(trigger)) return;
      event.preventDefault();
      openDialog(trigger);
    });

    dialog.querySelectorAll('[data-ash-size-guide-close]').forEach((button) => {
      button.addEventListener('click', closeDialog);
    });

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeDialog();
    });

    dialog.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      closeDialog();
    });

    dialog.addEventListener('close', () => {
      getTriggers().forEach((item) => item.setAttribute('aria-expanded', 'false'));
      document.body.classList.remove('ash-pdp-dialog-open');
      notify(false);
      returnFocus?.focus();
      returnFocus = null;
    });

    document.addEventListener('variant:changed', (event) => {
      const currentVariant = productInfo.currentVariant || getSelectedVariant(productInfo);
      if (event.detail?.variant?.id !== currentVariant?.id) return;
      requestAnimationFrame(prepareTriggers);
    });
  };

  const setupGallery = async (productInfo) => {
    await customElements.whenDefined('media-gallery');
    const gallery = productInfo.querySelector('media-gallery');
    const progress = productInfo.querySelector('[data-ash-media-progress]');
    const list = gallery?.querySelector('.m-media-gallery__list');
    if (!gallery || !progress || !list) return;

    const carouselQuery = window.matchMedia('(max-width: 1023px)');
    let resizeFrame = null;
    let boundSlider = null;

    const updateProgress = () => {
      const slides = Array.from(list.children).filter((slide) => !slide.classList.contains('swiper-slide-duplicate'));
      const total = slides.length;
      const current = gallery.slider && typeof gallery.slider === 'object'
        ? Math.min(total, Math.max(1, Number(gallery.slider.realIndex ?? gallery.slider.activeIndex ?? 0) + 1))
        : 1;

      gallery.dataset.activeMediaCount = String(total);
      progress.hidden = !carouselQuery.matches || total <= 1;
      progress.style.setProperty('--ash-media-progress', total > 0 ? String(current / total) : '0');
      progress.setAttribute('aria-valuenow', String(current));
      progress.setAttribute('aria-valuemax', String(total));
      progress.setAttribute('aria-valuetext', `Media ${current} of ${total}`);
    };

    const bindSlider = () => {
      if (!gallery.slider || typeof gallery.slider !== 'object' || gallery.slider === boundSlider) return;
      boundSlider = gallery.slider;
      gallery.slider.on('slideChange', updateProgress);
      gallery.slider.on('update', updateProgress);
    };

    const destroyNavSlider = () => {
      if (gallery.navSlider && typeof gallery.navSlider === 'object') {
        gallery.navSlider.destroy();
        gallery.navSlider = false;
      }
    };

    const applyLayout = () => {
      if (carouselQuery.matches) {
        if (!gallery.slider || typeof gallery.slider !== 'object') {
          destroyNavSlider();
          gallery.initSlider();
        }
        bindSlider();
      } else {
        gallery.destroySlider();
        destroyNavSlider();
        boundSlider = null;
      }
      requestAnimationFrame(updateProgress);
    };

    const scheduleLayout = () => {
      if (resizeFrame) cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(() => {
        resizeFrame = requestAnimationFrame(() => {
          resizeFrame = null;
          applyLayout();
        });
      });
    };

    const mediaObserver = new MutationObserver(() => {
      requestAnimationFrame(() => {
        bindSlider();
        updateProgress();
      });
    });
    mediaObserver.observe(list, { childList: true });

    document.addEventListener('variant:changed', (event) => {
      const currentVariant = productInfo.currentVariant || getSelectedVariant(productInfo);
      if (event.detail?.variant?.id !== currentVariant?.id) return;
      requestAnimationFrame(() => {
        bindSlider();
        updateProgress();
      });
    });
    carouselQuery.addEventListener?.('change', scheduleLayout);
    window.addEventListener('resize', scheduleLayout, { passive: true });

    applyLayout();
  };

  const initializeProduct = (productInfo) => {
    if (productInfo.dataset.ashInitialized === 'true') return;
    productInfo.dataset.ashInitialized = 'true';
    suppressReducedMotionAutoplay(productInfo.parentElement || productInfo);
    setupSizeGuide(productInfo);
    setupGallery(productInfo);
  };

  const initializeAll = (scope = document) => {
    scope.querySelectorAll(productSelector).forEach(initializeProduct);
  };

  suppressReducedMotionAutoplay();
  enhancePageChrome();
  initializeAll();

  reducedMotionQuery.addEventListener?.('change', () => suppressReducedMotionAutoplay());
  document.addEventListener('shopify:section:load', (event) => {
    enhancePageChrome();
    initializeAll(event.target);
  });
})();
